'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import TopBar from '@/components/TopBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const benefits = [
{
  title: 'Collateral-Driven Approval',
  description: 'Your assets do the talking. We secure financing based on the value of your receivables, inventory, or equipment — not just your credit score.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>

},
{
  title: 'Flexible Credit Lines',
  description: 'Draw down only what you need, when you need it. Our revolving credit structures scale with your business cycle.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>

},
{
  title: 'Higher Advance Rates',
  description: 'We negotiate advance rates of up to 90% on eligible receivables and 70% on inventory — significantly higher than traditional bank lines.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>

},
{
  title: 'Rapid Deployment',
  description: 'When a contract or opportunity arrives, you need capital fast. Our ABL facilities can be structured and funded in as little as 2–3 weeks.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>

},
{
  title: 'No Dilution of Equity',
  description: 'Unlike raising equity, ABL lets you fund growth without giving up ownership. Keep control of your business while accessing the capital you need.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>

},
{
  title: 'Industry-Agnostic',
  description: 'Manufacturing, distribution, staffing, construction, healthcare — if your business has assets, we can build a lending facility around them.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>

}];


const processSteps = [
{
  step: '01',
  title: 'Asset Assessment',
  description: 'We evaluate your eligible collateral — accounts receivable aging, inventory quality, equipment appraisals — to determine your borrowing base.'
},
{
  step: '02',
  title: 'Facility Structuring',
  description: 'We design the right facility type: revolving line of credit, term loan, or hybrid structure, matched to your cash conversion cycle.'
},
{
  step: '03',
  title: 'Lender Placement',
  description: 'We present your collateral package to ABL-specialist lenders who understand asset-intensive businesses and price risk accordingly.'
},
{
  step: '04',
  title: 'Term Negotiation',
  description: 'Advance rates, field exam frequency, reporting covenants — we negotiate every term to minimize operational burden on your team.'
},
{
  step: '05',
  title: 'Documentation & Legal',
  description: 'We coordinate with legal counsel to review security agreements, UCC filings, and intercreditor arrangements for a clean close.'
},
{
  step: '06',
  title: 'Ongoing Management',
  description: 'Post-close, we remain available to help you manage borrowing base certificates, lender reporting, and facility amendments as your business evolves.'
}];


const eligibleAssets = [
'Accounts Receivable',
'Inventory & Raw Materials',
'Equipment & Machinery',
'Purchase Orders',
'Real Property',
'Intellectual Property',
'Government Contracts',
'Trade Finance Assets'];


function AnimatedSection({ children, className = '' }: {children: React.ReactNode;className?: string;}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {if (entry.isIntersecting) setVisible(true);}, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>);

}

export default function AssetBasedLendingPage() {
  return (
    <main className="min-h-screen font-sans bg-white">
      <TopBar />
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[480px] flex items-center justify-center overflow-hidden">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_15168e4b7-1786982086373.png"
          alt="Business assets and financial documents representing asset-based lending"
          fill
          className="object-cover"
          priority />
        
        <div className="absolute inset-0 bg-brand-darkbg/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-24">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-brand-green" />
            <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Service 02</span>
            <div className="h-px w-12 bg-brand-green" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6 leading-tight">
            Asset-Based Lending
          </h1>
          <p className="font-sans text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Unlock the capital trapped in your business assets — receivables, inventory, and equipment — without diluting equity or waiting on traditional bank approvals.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
              
              Unlock Your Capital
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
              
              All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-0.5 bg-brand-green" />
                <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">What We Do</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy mb-6">
                Your Balance Sheet Is a Lending Facility Waiting to Be Activated
              </h2>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Asset-based lending (ABL) is one of the most powerful — and underutilized — financing tools available to growing businesses. Instead of relying solely on cash flow or credit history, ABL uses the tangible assets on your balance sheet as collateral to secure a revolving credit facility or term loan.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Grid Global specializes in structuring ABL facilities for businesses that need working capital to fund growth, bridge seasonal cash flow gaps, support acquisitions, or refinance expensive debt. We work with asset-intensive businesses across manufacturing, distribution, construction, staffing, and trade finance.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed">
                Our team knows which lenders specialize in which asset types, which advance rates are achievable, and how to structure facilities that give you maximum flexibility without onerous reporting requirements.
              </p>
            </AnimatedSection>
            <AnimatedSection className="grid grid-cols-2 gap-4">
              {eligibleAssets.map((asset, i) =>
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all duration-200">
                  <div className="w-2 h-2 rounded-full bg-brand-green flex-shrink-0" />
                  <span className="font-sans text-sm text-brand-navy font-medium">{asset}</span>
                </div>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-brand-darkbg">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Why Grid Global</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white">
              The Advantages of Working With Us
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) =>
            <AnimatedSection key={i} className="p-6 rounded-2xl bg-brand-altbg border border-white/10 hover:border-brand-green/40 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green mb-5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-serif text-lg font-medium text-white mb-3">{benefit.title}</h3>
                <p className="font-sans text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Our Process</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy">
              From Asset Audit to Active Credit Line
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, i) =>
            <AnimatedSection key={i}>
                <div className="p-8 rounded-2xl border border-gray-100 hover:border-brand-navy/20 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="font-serif text-5xl font-medium text-brand-navy/10 mb-4 leading-none">{step.step}</div>
                  <h3 className="font-serif text-xl font-medium text-brand-navy mb-3">{step.title}</h3>
                  <p className="font-sans text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-5">
              What Assets Are Sitting Idle on Your Balance Sheet?
            </h2>
            <p className="font-sans text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Let our team assess your collateral and show you exactly how much capital you could unlock — at no cost and no obligation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
                
                Get a Free Assessment
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/#services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
                
                Explore Other Services
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </main>);

}